import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  customLabels,
  fieldMapping,
} from "../../data/buildingPermit/fieldDatas";
import { setBuildingState } from "../../store/actions/permitFormActions/buildingStateActions";
import { formikRef } from "../../store/formikStore/formikStore";
import { addUserFullDesignProfessionals } from "../../store/actions/userActions";
import { addSubUserFullDesignProfessionals } from "../../store/actions/subUserActions";
import { addPPUserFullDesignProfessionals } from "../../store/actions/privateProviderActions";
import { LoadingButton } from "@mui/lab";
import {
  TEMPLATE_CATEGORY_IDS,
  getTemplateCategoryConfig,
} from "../permitForms/utils/templateCategoryHelpers";

const DESIGN_PRO_CATEGORY_IDS = new Set([
  TEMPLATE_CATEGORY_IDS.ENGINEER,
  TEMPLATE_CATEGORY_IDS.ARCHITECT,
  TEMPLATE_CATEGORY_IDS.DESIGN_PROFESSIONAL,
]);

const getFieldName = (field) => field?.name || field?.field_name;

const getFieldLabel = (field) => {
  const name = getFieldName(field);
  return (
    customLabels[name] ||
    field?.label ||
    field?.field_label ||
    field?.title ||
    String(name || "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

const DesignProAddInfoModal = () => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { addingNewDesignPro, designProLoading, activeTemplateCategory } =
    useSelector((state) => state.buildingState);

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [fieldValues, setFieldValues] = useState({
    design_pro_company: "",
    design_pro_license_name: "",
    design_pro_license_num: "",
    design_pro_address: "",
    design_pro_phone: "",
    design_pro_fax: "",
    design_pro_email: "",
  });

  const dynamicCategoryFields = useMemo(() => {
    const dynamicFields = DESIGN_PRO_CATEGORY_IDS.has(
      activeTemplateCategory?.categoryId,
    )
      ? activeTemplateCategory.fields || []
      : [];
    const fieldsById = new Map();

    dynamicFields.forEach((field) => {
      const name = getFieldName(field);
      const identity = field?.field_id || name;
      if (identity && name && !fieldsById.has(identity)) {
        fieldsById.set(identity, field);
      }
    });

    return Array.from(fieldsById.values());
  }, [activeTemplateCategory]);

  const dynamicCanonicalFieldNames = useMemo(() => {
    const config = getTemplateCategoryConfig(
      activeTemplateCategory?.categoryId,
    );

    return Object.entries(fieldMapping).reduce(
      (names, [canonicalName, savedPropertyName]) => {
        const matchingField = dynamicCategoryFields.find((field) => {
          const dynamicName = getFieldName(field);
          if (!dynamicName) return false;

          const isDynamicStreetForStaticAddress =
            canonicalName === "design_pro_address" &&
            /_address_street$/.test(dynamicName);

          return (
            dynamicName === canonicalName ||
            isDynamicStreetForStaticAddress ||
            (config?.aliases?.[dynamicName] || []).includes(savedPropertyName)
          );
        });
        const dynamicName = getFieldName(matchingField);

        return dynamicName ? { ...names, [canonicalName]: dynamicName } : names;
      },
      {},
    );
  }, [activeTemplateCategory?.categoryId, dynamicCategoryFields]);

  const resolvedFields = useMemo(() => {
    if (!dynamicCategoryFields.length) {
      return Object.keys(fieldMapping).map((name) => ({ name }));
    }

    const missingStaticFields = Object.keys(fieldMapping)
      .filter((name) => !dynamicCanonicalFieldNames[name])
      .map((name) => ({ name }));

    return [...dynamicCategoryFields, ...missingStaticFields];
  }, [dynamicCanonicalFieldNames, dynamicCategoryFields]);

  const resolvedFieldNames = useMemo(
    () => resolvedFields.map(getFieldName).filter(Boolean),
    [resolvedFields],
  );

  const canonicalFieldNames = useMemo(() => {
    if (!dynamicCategoryFields.length) {
      return Object.keys(fieldMapping).reduce(
        (names, name) => ({ ...names, [name]: name }),
        {},
      );
    }

    return Object.keys(fieldMapping).reduce(
      (names, canonicalName) => ({
        ...names,
        [canonicalName]:
          dynamicCanonicalFieldNames[canonicalName] || canonicalName,
      }),
      {},
    );
  }, [dynamicCanonicalFieldNames, dynamicCategoryFields.length]);

  const getCanonicalFieldValue = (canonicalName) => {
    const resolvedName = canonicalFieldNames[canonicalName];
    return resolvedName ? (fieldValues[resolvedName] ?? "") : "";
  };

  useEffect(() => {
    if (!addingNewDesignPro || !resolvedFieldNames.length) return;

    setFieldValues((prev) => ({
      ...prev,
      ...resolvedFieldNames.reduce(
        (values, name) => ({ ...values, [name]: prev[name] ?? "" }),
        {},
      ),
    }));
  }, [addingNewDesignPro, resolvedFieldNames]);

  useEffect(() => {
    const callFunc = () => {
      let error = false;

      Object.entries(canonicalFieldNames).forEach(([item, resolvedName]) => {
        if (item !== "design_pro_fax") {
          const val = String(fieldValues[resolvedName] ?? "");

          if (item === "design_pro_phone" || item === "design_pro_fax") {
            if (val === "" || val.length < 12) {
              error = true;
            }
          } else {
            if (val === "" || val.length < 3) {
              error = true;
            }
          }
        }
      });

      setSubmitDisabled(error);
    };

    callFunc();
  }, [canonicalFieldNames, fieldValues]);

  const runFieldCange = (event) => {
    const { name, value } = event.target;
    setFieldValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ designProLoading: true }));

      const categoryFieldValues = resolvedFieldNames.reduce(
        (values, name) => ({
          ...values,
          [name]: fieldValues[name] ?? "",
        }),
        {},
      );
      const fieldValuesById = resolvedFields.reduce((values, field) => {
        const fieldId = field?.field_id;
        const fieldName = getFieldName(field);

        return fieldId && fieldName
          ? { ...values, [fieldId]: fieldValues[fieldName] ?? "" }
          : values;
      }, {});
      const categoryId = activeTemplateCategory?.categoryId;

      const values = {
        ...categoryFieldValues,
        ...(categoryId && Object.keys(fieldValuesById).length
          ? {
              category_id: categoryId,
              field_values: fieldValuesById,
            }
          : {}),
        companyName: getCanonicalFieldValue("design_pro_company"),
        licenseName: getCanonicalFieldValue("design_pro_license_name"),
        licenseNumber: getCanonicalFieldValue("design_pro_license_num"),
        address: getCanonicalFieldValue("design_pro_address"),
        phone: getCanonicalFieldValue("design_pro_phone"),
        fax: getCanonicalFieldValue("design_pro_fax"),
        email: getCanonicalFieldValue("design_pro_email"),
        addData: true, // Flag to indicate we're adding new data
      };

      // Call API to save to database
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/modify-design-professional`,
        {
          values,
          currentSession: currentSession,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        const designProfessionals = (
          resp?.data?.design_professionals || []
        ).map((professional) => {
          const isNewProfessional =
            professional.companyName?.toLowerCase() ===
              values.companyName.toLowerCase() &&
            professional.email?.toLowerCase() === values.email.toLowerCase();

          return isNewProfessional
            ? {
                ...professional,
                ...categoryFieldValues,
                ...(categoryId && Object.keys(fieldValuesById).length
                  ? {
                      category_id: professional.category_id || categoryId,
                      field_values:
                        professional.field_values || fieldValuesById,
                    }
                  : {}),
              }
            : professional;
        });

        // Update Redux store with the saved design professionals from the database
        if (currentSession === "privateProviderSession") {
          await dispatch(addPPUserFullDesignProfessionals(designProfessionals));
        } else if (currentSession === "subUserSession") {
          dispatch(addSubUserFullDesignProfessionals(designProfessionals));
        } else if (currentSession === "userSession") {
          dispatch(addUserFullDesignProfessionals(designProfessionals));
        }

        // Find the newly added design professional to set it in the form
        const newDesignPro = designProfessionals.find(
          (pro) =>
            pro.companyName?.toLowerCase() ===
              values.companyName.toLowerCase() &&
            pro.email?.toLowerCase() === values.email.toLowerCase(),
        );

        if (newDesignPro?.id) {
          formik?.setValues((prevValues) => ({
            ...prevValues,
            ...fieldValues,
            designProfessional: newDesignPro.id,
          }));
        }

        await dispatch(
          setBuildingState({
            addingNewDesignPro: false,
            activeTemplateCategory: null,
          }),
        );
      } else {
        console.error("❌ API returned success: false", resp?.data);
        // Handle error cases
        if (resp?.data?.duplicated) {
          console.error("❌ Duplicate:", resp?.data?.message);
          alert(resp?.data?.message);
        } else if (resp?.data?.is_locked) {
          console.error("❌ Locked:", resp?.data?.message);
          alert(resp?.data?.message);
        }
      }
    } catch (e) {
      console.error("❌ Error adding design professional:", e);
      console.error("❌ Error details:", e?.response?.data || e?.message);
      alert("Failed to add design professional. Please try again.");
    } finally {
      await dispatch(setBuildingState({ designProLoading: false }));
    }
  };

  const cleanPhone = (e) => {
    const { name, value } = e.target;

    let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

    // If the number of digits is greater than 10, slice to 10 digits
    if (digitsOnly?.length > 10) {
      digitsOnly = digitsOnly.slice(0, 10);
    }

    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    setFieldValues((prev) => ({
      ...prev,
      [name]: formattedPhoneNumber,
    }));
  };

  return (
    <Modal
      open={addingNewDesignPro}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent", // Add a semi-transparent backdrop
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%", // Adjust width for smaller screens
          maxWidth: 600, // Set a maximum width
          bgcolor: "background.paper",
          boxShadow: "2px 10px 10px 5px rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" align="center" color="primary">
            Add New Design Professional
          </Typography>
        </Box>

        {/* Scrollable Content Section */}
        <Box
          sx={{
            maxHeight: "60vh", // Limit height to 60% of the viewport
            overflowY: "auto", // Enable scrolling when content overflows
            p: 2,
          }}
        >
          <>
            {resolvedFields.map((fieldConfig, i) => {
              const key = getFieldName(fieldConfig);
              const isPhoneField =
                key === "design_pro_phone" ||
                key === "design_pro_fax" ||
                /(^|_)(phone|mobile|fax)($|_)/i.test(key);

              return (
                <TextField
                  autoComplete="off"
                  key={key + i}
                  fullWidth
                  onKeyUp={(event) => {
                    if (isPhoneField) cleanPhone(event);
                  }}
                  margin="normal"
                  variant="filled"
                  label={getFieldLabel(fieldConfig)}
                  name={key}
                  value={fieldValues[key] ?? ""}
                  onChange={runFieldCange}
                  onBlur={(event) => {
                    formik?.handleBlur(event);
                    formik?.setFieldTouched(key, true, true);
                  }}
                  error={formik?.touched[key] && Boolean(formik?.errors[key])}
                  helperText={formik?.touched[key] && formik?.errors[key]}
                />
              );
            })}
          </>
        </Box>

        {/* Footer Section with Buttons */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            disabled={designProLoading}
            fullWidth
            onClick={() => {
              dispatch(
                setBuildingState({
                  addingNewDesignPro: false,
                  activeTemplateCategory: null,
                }),
              );
            }}
            color="error"
            variant="contained"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={designProLoading}
            disabled={submitDisabled}
            fullWidth
            onClick={handleAddNew}
            color="success"
            variant="contained"
          >
            Add
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(DesignProAddInfoModal);
